import React, { useState, useEffect } from 'react';
import { findSimilarTextures } from './TextureSimilarity';
import Chart from 'chart.js/auto';
import './TextureSimilarityFinder.css';

const TextureSimilarityFinder = ({ textures = [] }) => {
  // State variables to match the original implementation
  const [weights, setWeights] = useState({
    colorWeight: 0.30,
    tag1Weight: 0.20,
    tag2Weight: 0.20,
    tag3Weight: 0.20,
    materialWeight: 0.10,
    categoryWeight: 0.10,
  });
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [numResults, setNumResults] = useState(5);
  const [similarTextures, setSimilarTextures] = useState([]);
  const [chartInstances, setChartInstances] = useState([]);
  
  // Update weight value
  const updateWeight = (name, value) => {
    setWeights(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Find similar textures when parameters change
  useEffect(() => {
    if (textures.length === 0 || selectedIndex === null) return;
    
    // Find similar textures based on selected texture and weights
    const results = findSimilarTextures(textures, selectedIndex, numResults, weights);
    setSimilarTextures(results);
    
    // Clean up chart instances to prevent memory leaks
    chartInstances.forEach(chart => chart.destroy());
    setChartInstances([]);
  }, [textures, selectedIndex, numResults, weights, chartInstances]);

  // Create charts for each result after DOM is updated
  useEffect(() => {
    const newChartInstances = [];
    
    similarTextures.forEach((item, idx) => {
      const canvas = document.getElementById(`chartBreakdown_${idx}`);
      if (!canvas) return;
      
      const chart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: ['Score'],
          datasets: [
            { label: 'Color', data: [item.color], backgroundColor: '#ff6600' },
            { label: 'Tactile', data: [item.tactile], backgroundColor: 'green' },
            { label: 'Aesthetic', data: [item.aesthetic], backgroundColor: 'blue' },
            { label: 'Technical', data: [item.technical], backgroundColor: 'purple' },
            { label: 'Material', data: [item.material], backgroundColor: 'brown' },
            { label: 'Category', data: [item.category], backgroundColor: 'magenta' }
          ]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return context.dataset.label + ": " + context.parsed.x.toFixed(3);
                }
              }
            }
          },
          scales: {
            x: {
              stacked: true,
              beginAtZero: true
            },
            y: {
              stacked: true,
              ticks: { display: false }
            }
          }
        }
      });
      
      newChartInstances.push(chart);
    });
    
    setChartInstances(newChartInstances);
    
    // Clean up function to destroy charts when component unmounts
    return () => {
      newChartInstances.forEach(chart => chart.destroy());
    };
  }, [similarTextures]);

  return (
    <div className="texture-similarity-finder">
      <div className="main-controls">
        <div className="weight-controls">
          <h3>Similarity Weights</h3>
          <div id="sliders">
            <div className="slider-group">
              <label htmlFor="colorWeight">
                Color Weight: <span>{weights.colorWeight.toFixed(2)}</span>
              </label>
              <input
                type="range"
                id="colorWeight"
                min="0"
                max="1"
                step="0.01"
                value={weights.colorWeight}
                onChange={(e) => updateWeight('colorWeight', parseFloat(e.target.value))}
              />
            </div>
            <div className="slider-group">
              <label htmlFor="tag1Weight">
                Tactile Tag Weight: <span>{weights.tag1Weight.toFixed(2)}</span>
              </label>
              <input
                type="range"
                id="tag1Weight"
                min="0"
                max="1"
                step="0.01"
                value={weights.tag1Weight}
                onChange={(e) => updateWeight('tag1Weight', parseFloat(e.target.value))}
              />
            </div>
            <div className="slider-group">
              <label htmlFor="tag2Weight">
                Aesthetic Tag Weight: <span>{weights.tag2Weight.toFixed(2)}</span>
              </label>
              <input
                type="range"
                id="tag2Weight"
                min="0"
                max="1"
                step="0.01"
                value={weights.tag2Weight}
                onChange={(e) => updateWeight('tag2Weight', parseFloat(e.target.value))}
              />
            </div>
            <div className="slider-group">
              <label htmlFor="tag3Weight">
                Technical Tag Weight: <span>{weights.tag3Weight.toFixed(2)}</span>
              </label>
              <input
                type="range"
                id="tag3Weight"
                min="0"
                max="1"
                step="0.01"
                value={weights.tag3Weight}
                onChange={(e) => updateWeight('tag3Weight', parseFloat(e.target.value))}
              />
            </div>
            <div className="slider-group">
              <label htmlFor="materialWeight">
                Material Weight: <span>{weights.materialWeight.toFixed(2)}</span>
              </label>
              <input
                type="range"
                id="materialWeight"
                min="0"
                max="1"
                step="0.01"
                value={weights.materialWeight}
                onChange={(e) => updateWeight('materialWeight', parseFloat(e.target.value))}
              />
            </div>
            <div className="slider-group">
              <label htmlFor="categoryWeight">
                Category Weight: <span>{weights.categoryWeight.toFixed(2)}</span>
              </label>
              <input
                type="range"
                id="categoryWeight"
                min="0"
                max="1"
                step="0.01"
                value={weights.categoryWeight}
                onChange={(e) => updateWeight('categoryWeight', parseFloat(e.target.value))}
              />
            </div>
          </div>
        </div>

        <div className="selection-controls">
          <div className="control-group">
            <label htmlFor="textureSelect">Select Texture:</label>
            <select 
              id="textureSelect" 
              className="modern-select"
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(parseInt(e.target.value))}
            >
              {textures.map((texture, index) => (
                <option key={index} value={index}>
                  {texture.texture_name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="control-group">
            <label htmlFor="numSimilar">Show Results:</label>
            <input
              type="number"
              id="numSimilar"
              value={numResults}
              min="1"
              className="modern-input"
              onChange={(e) => setNumResults(parseInt(e.target.value) || 5)}
            />
          </div>
        </div>
      </div>
      
      {/* Selected texture details */}
      {textures[selectedIndex] && (
        <div id="selectedTexture">
          <h3>Selected Texture Details</h3>
          <p><strong>Name:</strong> {textures[selectedIndex].texture_name}</p>
          <p><strong>Material:</strong> {textures[selectedIndex].material_name}</p>
          <p><strong>Category:</strong> {textures[selectedIndex].category_name}</p>
          <p><strong>Description:</strong> {textures[selectedIndex].description || ''}</p>
        </div>
      )}
      
      {/* Similar textures list */}
      <h2>Similar Textures</h2>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Material</th>
            <th>Category</th>
            <th>Tactile Tags</th>
            <th>Aesthetic Tags</th>
            <th>Technical Tags</th>
            <th>Score</th>
            <th>Breakdown</th>
          </tr>
        </thead>
        <tbody id="similarList">
          {similarTextures.map((item, idx) => (
            <tr key={idx}>
              <td>{item.texture.texture_name}</td>
              <td>{item.texture.material_name}</td>
              <td>{item.texture.category_name}</td>
              <td>{item.texture.tactile_tags?.join(", ") || ""}</td>
              <td>{item.texture.aesthetic_tags?.join(", ") || ""}</td>
              <td>{item.texture.technical_tags?.join(", ") || ""}</td>
              <td>{item.total.toFixed(3)}</td>
              <td>
                <div className="chart-container">
                  <canvas id={`chartBreakdown_${idx}`}></canvas>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TextureSimilarityFinder; 